// TODO: Store in IndexedDB and sync on-demand/periodically.
import { SchedulesService } from '../services/schedules-service';
import {
    Mds,
    MdsStatus,
    VesselPositionData,
    PremiumVesselsList,
    VesselCategories,
    VesselPositionDataExtended
} from '../services/types/position-service.types';
import { formatToTitleCase } from '../utils/helpers';
import { DbModel } from './db-model';
import { VesselAIS, VesselInfo,categories,validOperator,maerskOwnershipTypes ,nonMaerskWithBox, GlobalSearchsVesselsMetaData, GlobalFilterVesselMetaData} from './types/vessels-model.types';
import { CurrentScheduleState } from './types/voyage-schedules-model.types';
import { VesselsModelBase } from './vessels-model';
import { VesselsTrackingModel } from './vessels-tracking-model';
import { VoyFleetAlarmModel } from './voy-fleet-alarm-model';
import { AisStatusId, OperatorId, VesselVoyageVersion } from '../../local_modules/voy-global-filters/src/types';
import { VoyFleetNotificationModel } from './voy-fleet-notification-model';
import { AlarmType, RouteTypeOption, RouteTypeOptionComingFromApi } from '../services/types/fleet-alarms-service.types';
import { RouteTypePost, RouteTypesPayload, VoyageRoutingModel } from './voyage-routing-model';

const _POSITION_OBSOLETE = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

export class VesselsModelImpl extends VesselsModelBase {
    constructor() {
        super();

        this.refreshVessels = this.refreshVessels.bind(this);

        setInterval(this.refreshVessels, 4*60*60*1000); // update every hour
    }

    get maerskVessels(): Map<number,VesselInfo> {
        return this._maerskVessels
    }

    get nonMaerskVessels(): Map<number,VesselInfo> {
        return this._nonMaerskVessels
    }

    private _globalFilterMetadataNonMaerskVessels: GlobalFilterVesselMetaData[] = []
    private _globalFilterMetadataMaerskVessels: GlobalFilterVesselMetaData[] = []

    get globalFilterMetadataMaerskVessels(): GlobalFilterVesselMetaData[] {
        return this._globalFilterMetadataMaerskVessels
    }

    get globalFilterMetadataNonMaerskVessels(): GlobalFilterVesselMetaData[] {
        return this._globalFilterMetadataNonMaerskVessels
    }

    get globalSearchVesselsMetaData(): GlobalSearchsVesselsMetaData[] {
        return this._globalSearchVesselsMetaData;
    }

    get globalSearchMaerskVesselsMetaData(): GlobalSearchsVesselsMetaData[] {
        return this._globalSearchMaerskVesselsMetaData;
    }

    get globalSearchNonMaerskVesselsMetaData(): GlobalSearchsVesselsMetaData[] {
        return this._globalSearchNonMaerskVesselsMetaData;
    }

    getFilterCategoryCount(id:string) {    
        return this._filterCategoryCount.get(id)
    }

    getVessel(imoNo: number) {
        let vessel: VesselInfo| undefined;
        if (this._maerskVessels.has(imoNo)) {
            vessel = this._maerskVessels.get(imoNo);
        }
        if (this._nonMaerskVessels.has(imoNo)) {
            vessel = this._nonMaerskVessels.get(imoNo);
        }
        const ais = this._AISContainerVessels.get(imoNo)

        if (vessel && ais) {
            return Object.assign(vessel, { ais })
        }

        return vessel;
    }

    getVesselName(imoNo: number) {
        const vessel = this.getVessel(imoNo);
        if (vessel && vessel.vesselName) {
            return vessel.vesselName;
        }

        return this._AISContainerVessels.get(imoNo)?.name
    }

    getAllVesselsClasses(): string[] {
        return Array.from(this._vesselClasses).sort((a,b) => a.localeCompare(b));
    }

    async refreshVessels() {
        this._globalFilterMetadataNonMaerskVessels =[]
        this._globalFilterMetadataMaerskVessels = []
        const ts = (new Date()).getTime();
        const schedulesStatesPromise = SchedulesService.fetchScheduleVesselStates();
        const schedulesStates = await schedulesStatesPromise;
        let schedulesMap: Map<number, CurrentScheduleState> = new Map();
        const payload: RouteTypesPayload[] = []

        schedulesStates.forEach((schedulesState: { imoNo: number; schedule: CurrentScheduleState; }) => {
            schedulesMap.set(schedulesState.imoNo, schedulesState.schedule);
            if(schedulesState.schedule.id !== ""){
                payload.push({
                    imo: schedulesState.imoNo,
                    schedule_id: schedulesState.schedule.id,
                })
            }
        })
        const routeTypeAll = VoyageRoutingModel?.routeTypeFilter(payload);
        const routeTypePost: RouteTypePost[] = (await routeTypeAll) || []; 
        const routeTypeMap = new Map();
            routeTypePost?.forEach((route: RouteTypePost) => {
                routeTypeMap.set(route.imo, route.route_type);
            });

        const allPositionsData = await DbModel?.getAllPositionsRecords();
        //filter valid imos - valid imos should have 7 digits 
        const allPositions = allPositionsData?.filter(position => position.imo!.toString()!.length === 7).map(position => {
            const routeTypeData = routeTypeMap.get(position.imo);
            return {
                ...position,
                routeType: routeTypeData || null 
            };
        });
        if (!allPositions) {
            if (VesselsTrackingModel!.loadingPositionsPromise) {
                await VesselsTrackingModel!.loadingPositionsPromise;
            } else {
                return;
            }
        };

        const allVesselsData = await DbModel?.getAllVesselsRecords();
        const allVessels = allVesselsData?.filter(val=>val.imoNo!.toString()!.length === 7);
        if (allVessels) {
            this._maerskVessels = new Map(allVessels.filter(vessel => vessel.operatorCode !== "nonMSK").map(obj => [obj.imoNo, obj]));
            this._nonMaerskVessels = new Map(allVessels.filter(vessel => vessel.operatorCode === "nonMSK").map(obj => [obj.imoNo, obj]));
            this.dispatchEvent(new Event('vessels-updated'));
        }
        
        this._maerskVessels.clear();
        this._nonMaerskVessels.clear();
        this._filterCategoryCount.clear();
        this._globalSearchVesselsMetaData = [];
        this._vesselClasses.clear();

        const getVesselPositionData = (p: VesselPositionDataExtended)  => {
            // Remove the vessels having age older than 30 days
            const positionAge = (new Date().getTime() - new Date(p.timestamp).getTime());
            if (positionAge <= _POSITION_OBSOLETE ) {
                const imo = p.imo;
                const AISContainerVesselData = this._createAisData(p);
                let curValue=0
                const aisNavStatus = this._getAisNavStatusID(p.navStatus);
                if(aisNavStatus){
                    curValue = (this._filterCategoryCount?.get(aisNavStatus) ?? 0) +1;
                    this._filterCategoryCount.set(aisNavStatus, curValue);
                }
                
                if (this._isMaerskVessel(p)){
                    const fleetGroup =this._getFleetGroup(p.mds);
                    const schedule = schedulesMap.get(imo);
                    let curVal;
                    if(schedule?.inPort) {
                        curVal = (this._filterCategoryCount?.get("in-port") ?? 0) + 1;
                        this._filterCategoryCount.set("in-port",curVal)
                    }
                    else{
                        curVal = (this._filterCategoryCount?.get("on-voyage")?? 0) +1
                        this._filterCategoryCount.set("on-voyage",curVal)                
                    } 
                    const serviceCode = schedule?.serviceCode ? schedule.serviceCode : null;

                    const operatorCode = p ?.mds?.operator?.code  || "";
                    const alarms = VoyFleetAlarmModel!.getVesselAlarmsBasicInfo(imo); 
                    const ownershipType = AISContainerVesselData.owner || "";
                    let operatorCodeVal = operatorCode;
                    if(!validOperator.includes(operatorCode) && maerskOwnershipTypes.includes(ownershipType)){
                        operatorCodeVal = OperatorId.MSKUWN
                    }
                    if (ownershipType && nonMaerskWithBox.includes(ownershipType)){
                        curVal = (this._filterCategoryCount?.get("NonMSKWithBox")?? 0) +1
                        this._filterCategoryCount.set("NonMSKWithBox", curVal) 
                    }
                    curVal = (this._filterCategoryCount?.get(operatorCodeVal) ?? 0) +1
                    let maerskCount = (this._filterCategoryCount?.get("maersk") ?? 0) +1
                    if (validOperator.includes(operatorCodeVal) || operatorCodeVal ==="MskUnwn") {
                        this._filterCategoryCount.set(operatorCodeVal, curVal)                
                        this._filterCategoryCount.set("maersk", maerskCount) 
                    }
            
                    const category = p.vesselCategory;
                    curVal = (this._filterCategoryCount?.get(category) ?? 0) +1;
                    this._filterCategoryCount.set(category, curVal);
            
                    if (fleetGroup) {
                        curVal = (this._filterCategoryCount?.get(fleetGroup) ?? 0) +1
                        this._filterCategoryCount.set(fleetGroup, curVal)
                    }

                    if (alarms.length) {
                        alarms.map((val) => {
                            //Remove this condition once eta-deviation notification generation logic change from fleet side reverted
                                curVal = (this._filterCategoryCount?.get(val.alarmType) ?? 0) +1
                                this._filterCategoryCount.set(val.alarmType, curVal) 
                        })
                    }
                   
                    const vesselCode = p.mds?.operator?.vesselCode ?? null;
                    const vesselName = formatToTitleCase(p.mds?.basic?.name || p.vesselName) || 'Name not found'
                    const vesselVersion = this._getVesselVersion(p.mds);
                    const vesselClass = p.mds?.basic?.vesselClass;
                    const hasPAPI = p.mds?.general?.primaryReportingTool === 1 ? true : false;

                    if (vesselVersion === VesselVoyageVersion.Premium) {
                        curVal = (this._filterCategoryCount?.get("premium") ?? 0) +1
                        this._filterCategoryCount.set("premium", curVal) 
                    }
                    else if(vesselVersion === VesselVoyageVersion.Basic) {
                        curVal = (this._filterCategoryCount?.get("basic") ?? 0) +1
                        this._filterCategoryCount.set("basic", curVal) 
                    }
                    else if(vesselVersion === VesselVoyageVersion.Cloud) {
                        curVal = (this._filterCategoryCount?.get("cloud") ?? 0) +1
                        this._filterCategoryCount.set("cloud", curVal) 
                    }
                    else if(vesselVersion === VesselVoyageVersion.Blank) {
                        curVal = (this._filterCategoryCount?.get("blank") ?? 0) +1
                        this._filterCategoryCount.set("blank", curVal) 
                    }

                    this._vesselClasses.add(p.mds?.basic?.vesselClass ?? "N/A")

                    if (vesselClass) {
                        curVal = (this._filterCategoryCount?.get(p.mds?.basic?.vesselClass!) ?? 0) + 1;
                        this._filterCategoryCount.set(p.mds?.basic?.vesselClass!, curVal);
                    }
                    if (p.routeType) {
                        const routeMap = {
                            [RouteTypeOptionComingFromApi.StarRoute]: RouteTypeOption.StarRoute,
                            [RouteTypeOptionComingFromApi.WniRoute]: RouteTypeOption.WniRoute,
                            [RouteTypeOptionComingFromApi.OwnRoute]: RouteTypeOption.OwnRoute,
                        };
                    
                        for (const [apiRoute, filterRoute] of Object.entries(routeMap)) {
                            if (p.routeType.includes(apiRoute)) {
                                const curVal = (this._filterCategoryCount?.get(filterRoute) ?? 0) + 1;
                                this._filterCategoryCount.set(filterRoute, curVal);
                            }
                        }
                    }                    
                    this._maerskVessels.set(imo, {
                        imoNo: imo,
                        vesselName,
                        vesselClass: vesselClass ?? "N/A",
                        ts: ts,
                        hasPAPI: hasPAPI,
                        ownership: p.mds?.basic?.ownershipType,
                        fg: fleetGroup,
                        callSign: AISContainerVesselData?.callSign ?? "N/A",
                        serviceCode: serviceCode,
                        operatorCode: operatorCodeVal || null,
                        navStatus: p.navStatus,
                        readingType: p.readingType,
                        alarms,
                        source: p.source,
                        ownershipType: AISContainerVesselData.owner,
                        vesselCode,
                        vesselVersion: vesselVersion,
                        vesselType: p.vesselType,
                        routeType: p.routeType
                    });
                    
                    this._globalSearchVesselsMetaData.push({
                        vesselName: formatToTitleCase(p.mds?.basic?.name || p.vesselName) || null,
                        vesselClass: vesselClass ?? null,
                        vesselDesc: p?.vesselDesc,
                        imo,
                        vesselCode: p.mds?.operator?.vesselCode ?? null,
                        vesselType: p.vesselType,
                        vesselVersion: vesselVersion
                    })

                    this._globalSearchMaerskVesselsMetaData.push({
                        vesselName: formatToTitleCase(p.mds?.basic?.name || p.vesselName) || null,
                        vesselClass: vesselClass ?? null,
                        vesselDesc: p?.vesselDesc,
                        imo,
                        vesselCode: p.mds?.operator?.vesselCode ?? null,
                        vesselType: p.vesselType,
                        vesselVersion: vesselVersion
                    })

                    this._globalFilterMetadataMaerskVessels.push({
                        alarms,
                        imoNo: imo,
                        operatorCode: operatorCodeVal || null,
                        serviceCode: serviceCode,
                        vesselClass: vesselClass ?? "N/A",
                        ownershipType: AISContainerVesselData.owner,
                        fg: fleetGroup,
                        source: vesselVersion,
                        vesselCategory: p.vesselCategory,
                        navStatus: aisNavStatus,
                        routeType: p.routeType
                    })

                } else {
                    let curVal = (this._filterCategoryCount?.get("nonMSK") ?? 0) + 1;
                    const category = p.vesselCategory;
                    if (category === VesselCategories.ContainerCargo) {
                        this._filterCategoryCount.set("nonMSK", curVal)
                    }


                    curVal = (this._filterCategoryCount?.get(category) ?? 0) +1;
                    this._filterCategoryCount.set(category, curVal);

                    const vessel: VesselInfo = {
                        alarms: [],
                        callSign: AISContainerVesselData?.callSign ?? "",
                        hasPAPI: false,
                        imoNo: imo,
                        operatorCode: 'nonMSK',
                        serviceCode: null,
                        ts: ts,
                        vesselClass: p.vesselClass ?? "N/A",
                        vesselName: formatToTitleCase(p.mds?.basic?.name || p.vesselName) || 'Name not found',
                        ais: AISContainerVesselData,
                        fg: undefined,
                        ownership: AISContainerVesselData?.owner,
                        source: p.source,
                        readingType: p.readingType,
                        navStatus: p.navStatus,
                        vesselType: p.vesselType,
                        routeType: p.routeType
                    }

                    this._nonMaerskVessels.set(imo, vessel);

                    this._globalSearchVesselsMetaData.push({
                        vesselName: formatToTitleCase(p.mds?.basic?.name || p.vesselName) || null,
                        vesselDesc: p.vesselDesc,
                        vesselClass: null,
                        vesselCode: null,
                        imo,
                        vesselType: p.vesselType
                    })

                    this._globalSearchNonMaerskVesselsMetaData.push({
                        vesselName: formatToTitleCase(p.mds?.basic?.name || p.vesselName) || null,
                        vesselDesc: p.vesselDesc,
                        vesselClass: null,
                        vesselCode: null,
                        imo,
                        vesselType: p.vesselType
                    })

                    this._globalFilterMetadataNonMaerskVessels.push({
                        alarms: [],
                        imoNo: imo,
                        ownershipType: AISContainerVesselData?.owner,
                        operatorCode: 'nonMSK',
                        serviceCode: null,
                        vesselClass: "unknown",
                        vesselCategory: p.vesselCategory,
                        navStatus: aisNavStatus
                    })
                }
                this._AISContainerVessels.set(imo, AISContainerVesselData)
            }
        }
        allPositions!.forEach(getVesselPositionData)

        //Dispatching event to check if vessels are loaded
        this.dispatchEvent(new Event('vessels-loaded'));
        DbModel?.populateVesselsTable(this._maerskVessels, this._nonMaerskVessels);
        this.dispatchEvent(new Event('vessels-updated'));
    }

    async plotVessel(imoNo: number) {
        if (!imoNo) {
            return;
        }

        this.dispatchEvent(new CustomEvent('plot-vessel', { detail: { imoNo } }));
    }

    private _maerskVessels: Map<number, VesselInfo> = new Map();
    private _nonMaerskVessels: Map<number, VesselInfo> = new Map();
    private _AISContainerVessels: Map<number, VesselAIS> = new Map();
    private _filterCategoryCount: Map<string | null, number> = new Map((categories.map((obj) => [obj, 0])));
    private _globalSearchVesselsMetaData: GlobalSearchsVesselsMetaData[] = [];
    private _globalSearchMaerskVesselsMetaData: GlobalSearchsVesselsMetaData[] = [];
    private _globalSearchNonMaerskVesselsMetaData: GlobalSearchsVesselsMetaData[] = [];
    private _vesselClasses: Set<string> = new Set();

    private _createAisData(vesselData: VesselPositionData): VesselAIS {
        const { vesselName,imo, callSign, vesselFlag, vesselYear, vesselLength, vesselWidth, draught, navStatus, source, mds, vesselClass } = vesselData

        const ais:VesselAIS = {
            name: vesselName,
            class: vesselClass,
            callSign: callSign ?? "",
            flag: mds?.basic?.flagState,
            yearBuilt: this._getYearBuilt(mds),
            length: vesselLength,
            width: vesselWidth,
            operator: '',
            draught,
            navStatus,
            dataSource:source,
            owner: mds?.basic?.ownershipType,
        }

        return ais;
    }

    private _getYearBuilt(mds:Mds|undefined){
        if(mds?.basic?.builtDate){
            return new Date(mds.basic.builtDate).getFullYear()
        }
        return undefined;
    }

    private _isMaerskVessel(vesselData: VesselPositionData) {
        // maersk vessel should contain mds object and status should not be outfleeted(outfleeted status for sold out vessels)
        return vesselData.mds && vesselData?.mds?.basic?.status !== MdsStatus.OUTFLEETED
    }

    private _getFleetGroup(mds:Mds|undefined){
        //need to trim the firxt 6 characters from the fleet group value received from 3MDS.
        if(mds?.basic) return mds.basic.fleetGroup?.slice(6)?.toLocaleLowerCase();
    }

    private _getAisNavStatusID(navstatus: string) {
        switch(navstatus){
            case "Under way using engine" : return AisStatusId.UnderWayUsingEngine;
            case "At anchor" : return AisStatusId.AtAnchor;
            case "Moored" : return AisStatusId.Moored;
            case "Not under command" : return AisStatusId.NotUnderCommand;
            case "Aground" : return AisStatusId.Aground;
            case "Constrained by her draught" : return AisStatusId.ConstrainedByHerDraught;
            case "Restricted maneuverability" : return AisStatusId.RestrictedManoeuvrability;
            case "Undefined" : return AisStatusId.Undefined;
            default: return AisStatusId.Other;
        }
    }

    private _getVesselVersion(mds: Mds | undefined): VesselVoyageVersion|undefined {
        if (!mds || mds.basic?.ownershipType === "VSA" || mds.basic?.ownershipType === "FOREIGN_FEEDER") {
            return undefined;
        }    
    
        switch (mds.general?.primaryVoyagePlanningTool) {
            case 1:
                return VesselVoyageVersion.Premium;
            case 2:
                return VesselVoyageVersion.Basic;
            case 3:
                return VesselVoyageVersion.Cloud;
            default:
                return VesselVoyageVersion.Blank;
        }
    }
}
